import { useState } from 'react'
import { importSiteApi, type ImportAnalysis } from '../utils/api'

// ── §64 Import Existing Website ───────────────────────────────────────────────

type Step = 'url' | 'analyzing' | 'review' | 'creating' | 'done'

interface Props {
  onClose: () => void
  onCreated?: (siteId: string, slug: string) => void
}

const SECTION_LABELS: Record<string, string> = {
  navbar: 'Навигация',
  hero: 'Главный экран',
  features: 'Преимущества',
  pricing: 'Тарифы',
  contact: 'Контакты',
  cta: 'Призыв к действию',
  footer: 'Подвал',
}

export function ImportWebsiteModal({ onClose, onCreated }: Props) {
  const [step, setStep] = useState<Step>('url')
  const [url, setUrl] = useState('')
  const [analysis, setAnalysis] = useState<ImportAnalysis | null>(null)
  const [siteName, setSiteName] = useState('')
  const [siteSlug, setSiteSlug] = useState('')
  const [error, setError] = useState('')

  async function handleAnalyze() {
    if (!url.trim()) return
    let normalized = url.trim()
    if (!normalized.startsWith('http')) normalized = 'https://' + normalized
    setError('')
    setStep('analyzing')
    try {
      const result = await importSiteApi.analyzeUrl(normalized)
      setAnalysis(result.analysis)
      setSiteName(result.suggestedName)
      setSiteSlug(result.suggestedSlug)
      setStep('review')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка анализа')
      setStep('url')
    }
  }

  async function handleCreate() {
    if (!analysis || !siteName.trim()) return
    setStep('creating')
    setError('')
    try {
      const result = await importSiteApi.createFromImport(analysis, siteName.trim(), siteSlug.trim())
      setStep('done')
      setTimeout(() => {
        onCreated?.(result.id, result.slug)
        onClose()
      }, 1500)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка создания')
      setStep('review')
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--bg-1)', borderRadius: 16, width: '100%', maxWidth: 560, boxShadow: '0 24px 64px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-primary)' }}>🌐 Импорт сайта по URL</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Анализ структуры существующего сайта</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ padding: '24px' }}>
          {/* Step: URL input */}
          {(step === 'url' || step === 'analyzing') && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                Введите URL существующего сайта. Система проанализирует его структуру и создаст новый проект на основе обнаруженных секций.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="url"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') void handleAnalyze() }}
                  placeholder="https://example.com"
                  disabled={step === 'analyzing'}
                  style={{ flex: 1, padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, background: 'var(--bg-2)', color: 'var(--text-primary)' }}
                />
                <button
                  onClick={() => void handleAnalyze()}
                  disabled={!url.trim() || step === 'analyzing'}
                  style={{ padding: '10px 20px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: !url.trim() || step === 'analyzing' ? 'not-allowed' : 'pointer', opacity: !url.trim() || step === 'analyzing' ? 0.7 : 1, whiteSpace: 'nowrap' }}
                >
                  {step === 'analyzing' ? 'Анализ...' : 'Анализировать'}
                </button>
              </div>
              {step === 'analyzing' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'var(--bg-2)', borderRadius: 10, border: '1px solid var(--border)' }}>
                  <div style={{ width: 16, height: 16, border: '2px solid var(--accent)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Загружаем страницу и анализируем структуру...</span>
                </div>
              )}
              {error && (
                <div style={{ padding: '8px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 12 }}>{error}</div>
              )}
            </div>
          )}

          {/* Step: Review analysis */}
          {step === 'review' && analysis && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Analysis result */}
              <div style={{ background: 'var(--bg-2)', borderRadius: 10, border: '1px solid var(--border)', overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Результат анализа</span>
                  {analysis.fetchError && (
                    <span style={{ fontSize: 11, padding: '2px 8px', background: '#fef3c7', color: '#92400e', borderRadius: 6 }}>Частичный анализ</span>
                  )}
                </div>
                <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    <span style={{ fontWeight: 700 }}>URL: </span>
                    <span style={{ fontFamily: 'monospace' }}>{analysis.url}</span>
                  </div>
                  {analysis.description && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      <span style={{ fontWeight: 700 }}>Описание: </span>{analysis.description}
                    </div>
                  )}
                  {analysis.fetchError && (
                    <div style={{ fontSize: 12, color: '#92400e' }}>
                      ⚠️ {analysis.fetchError} — создаём шаблон на основе стандартной структуры
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6 }}>
                      Обнаружено секций: {analysis.detectedSections.length}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {analysis.detectedSections.map(sec => (
                        <span key={sec} style={{ fontSize: 11, padding: '3px 10px', background: 'var(--accent)', color: '#fff', borderRadius: 6, opacity: 0.85 }}>
                          {SECTION_LABELS[sec] ?? sec}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Site name & slug */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 6 }}>
                    Название сайта
                  </label>
                  <input
                    value={siteName}
                    onChange={e => setSiteName(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, background: 'var(--bg-2)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 6 }}>
                    URL-адрес (slug)
                  </label>
                  <input
                    value={siteSlug}
                    onChange={e => setSiteSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, background: 'var(--bg-2)', color: 'var(--text-primary)', fontFamily: 'monospace', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {error && (
                <div style={{ padding: '8px 12px', background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 12 }}>{error}</div>
              )}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', paddingTop: 4 }}>
                <button onClick={() => { setStep('url'); setAnalysis(null) }} style={{ padding: '9px 18px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, color: 'var(--text-primary)', cursor: 'pointer' }}>
                  ← Назад
                </button>
                <button
                  onClick={() => void handleCreate()}
                  disabled={!siteName.trim() || !siteSlug.trim()}
                  style={{ padding: '9px 20px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: !siteName.trim() ? 'not-allowed' : 'pointer', opacity: !siteName.trim() ? 0.7 : 1 }}
                >
                  🌐 Создать сайт
                </button>
              </div>
            </div>
          )}

          {/* Step: Creating */}
          {step === 'creating' && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ width: 32, height: 32, border: '3px solid var(--accent)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Создаём сайт...</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>Генерируем страницы на основе структуры</div>
            </div>
          )}

          {/* Step: Done */}
          {step === 'done' && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#16a34a' }}>Сайт создан!</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>Переходим в редактор...</div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
